import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { Subscription } from 'rxjs';
import { TechnicianService } from 'src/app/services/technician.service';
import { prevTaskService } from 'src/app/services/prev-task.service';
import { ToastrService } from 'ngx-toastr';
import { BaseApi } from 'src/backend/api/base-api';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { TranslaterService } from 'src/app/services/translater.service';
import { UserConfigurationsService } from 'src/app/services/user-configurations.service';

@Component({
  selector: 'app-subtasks-dialog',
  template: `
    <div mat-dialog-title>
      <div class="d-flex justify-content-between align-items-center">
        <h2>{{ langObj?.SubTask || ('SubTask' | translate) }}</h2>
        <button mat-mini-fab mat-button (click)="closeDialog()" color="warn" title="close">
          <i class="fa fa-times" style="color: #fff"></i>
        </button>
      </div>
    </div>

    <mat-dialog-content>
      <ag-grid-angular
        #agGrid
        style="width: 100%; height: 400px;"
        class="ag-theme-alpine"
        [rowData]="rowData"
        [columnDefs]="columnDefs"
        [stopEditingWhenCellsLoseFocus]="true"
        [defaultColDef]="defaultColDef"
        (gridReady)="onGridReady($event)"
        [pagination]="true"
        [paginationPageSize]="10"
        [context]="context"
        (cellValueChanged)="onCellValueChanged($event)"
      ></ag-grid-angular>
    </mat-dialog-content>
  `,
})
export class SubtasksDialogComponent implements OnInit {
  @ViewChild('agGrid') agGrid!: AgGridAngular;

  userSession: any;
  rowData: any[] = [];
  isAssignedToMainTask: boolean = false;
  languageDir = 'ltr';
  langObj: any = {};
  moduleId!: any;
  pageId!: any;
  gridApi: any;
  gridColumnApi: any;
  isGridReady: boolean = false;
  isDataReady: boolean = false;
  editableWoId: number | null = null;

  columnDefs: any[] = [
    {
      headerName: 'WO Details',
      field: 'Detail',
      headerValueGetter: this.localizeHeader.bind(this)
    },
    {
      headerName: 'Responsible',
      field: 'ResponsibleName',
      width: 150,
      headerValueGetter: this.localizeHeader.bind(this)
    },
    {
      headerName: 'External Vendor',
      field: 'ExternalVendor',
      headerValueGetter: this.localizeHeader.bind(this)
    },
    {
      headerName: 'Status',
      field: 'Status',
      editable: (params: any) => {
        const currentUserCode = this.userSession?.userCode;
        const isResponsible = params.data.ResponsibleID === currentUserCode;
        const isCompleted = params.data.Status === 'Completed';
        return isResponsible && !isCompleted;
      },
      headerValueGetter: this.localizeHeader.bind(this),
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: ['Pending', 'Completed'],
      },
      cellStyle: (params: any) => {
        const isResponsible = params.data.ResponsibleID === this.userSession?.userCode;
        const isCompleted = params.data.Status === 'Completed';
        const style: any = {
          fontWeight: 'bold',
          color: 'black'
        };

        if (params.value === 'Completed') {
          style.backgroundColor = '#38cf36'; // green
        } else if (params.value === 'Pending') {
          style.backgroundColor = 'yellow';
        }

        return style;
      }
    },
    {
      headerName: 'Target Date',
      field: 'TargetDate',
      headerValueGetter: this.localizeHeader.bind(this),
      valueFormatter: (params: any) => params.value ? this.formatCellData(params.value, 'dateOnly') : null,
    },
    {
      headerName: 'Completion Date',
      field: 'CompletionDate',
      headerValueGetter: this.localizeHeader.bind(this),
      valueFormatter: (params: any) => params.value ? this.formatCellData(params.value, 'dateTime') : null,
    },
    {
      headerName: 'Created By',
      field: 'CreatedByName',
      headerValueGetter: this.localizeHeader.bind(this),
    },
    {
      headerName: 'Updated By',
      field: 'UpdatedByName',
      headerValueGetter: this.localizeHeader.bind(this),
    },
  ];

  defaultColDef = {
    cellStyle: (params: any) => {
      const isResponsible = params.data?.ResponsibleID === this.userSession?.userCode;
      return isResponsible
        ? {}
        : {
          pointerEvents: 'none',
          opacity: '0.5',
          backgroundColor: '#f0f0f0',
        };
    }
  };

  context = {
    getEditableWoId: () => this.editableWoId,
  };

  constructor(
    private translater: TranslaterService,
    private translate: TranslateService,
    private baseApi: BaseApi,
    private technicianService: TechnicianService,
    private prevTaskService: prevTaskService,
    private toaster: ToastrService,
    public dialogRef: MatDialogRef<SubtasksDialogComponent>,
    private userConfigService: UserConfigurationsService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.languageDir = event.lang === 'ar' ? 'rtl' : 'ltr';
      this.getLangWords();
    });
  }

  ngOnInit(): void {
    this.getLangWords();
    this.userSession = this.baseApi.getUserSession();
    if (this.data?.rowData) {
      this.rowData = this.data.rowData;
      this.isAssignedToMainTask = this.data.isAssignedToMainTask;
      this.isDataReady = true;
    }
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.isGridReady = true;

    const shouldShowCreatedBy = this.rowData.some(row => row.WOID > 0 || row.WOID !== undefined);

    this.columnDefs = shouldShowCreatedBy
      ? this.columnDefs
      : this.columnDefs.filter(col => col.field !== 'CreatedByName');
    this.columnDefs = shouldShowCreatedBy
      ? this.columnDefs
      : this.columnDefs.filter(col => col.field !== 'UpdatedByName');
    this.setGridDataIfReady();
  }

  setGridDataIfReady(): void {
    if (this.isGridReady && this.isDataReady) {
      this.gridApi.setColumnDefs(this.columnDefs);
      this.gridApi.setRowData(this.rowData);
      this.gridApi.sizeColumnsToFit();
    }
  }

  formatCellData(value: any, formatType: string): string {
    return this.userConfigService.formatField(value, formatType);
  }

  onCellValueChanged(event: any): void {
    if (event.colDef.field === 'Status') {
      const payload: any = {
        Id: event.data.Id,
        TaskId: event.data.TaskId,
        Detail: event.data.Detail,
        ResponsibleID: event.data.ResponsibleID,
        ExternalVendor: event.data.ExternalVendor,
        TargetDate: event.data.TargetDate ? new Date(event.data.TargetDate).toISOString() : null,
        CompletionDate: event.data.Status === 'Completed' ? new Date().toISOString() : null,
        Status: event.data.Status,
        RMPM: event.data.RMPM
      };

      if (event.data.WOID > 0) {
        payload.WOID = event?.data?.WOID;
        payload.TechnicianExecutionWOID = event?.data?.TechnicianExecutionWOID;
      } else {
        payload.TechnicianTaskId = event.data.TechnicianTaskId;
        payload.TaskId = event.data.TaskId;
      }

      this.prevTaskService.updateSubTaskWorkOrder(payload).subscribe({
        next: () => {
          this.toaster.success('SubTask Work Order Updated Successfully');
          const fetchData: any = {
            isAssignedToMainTask: this.isAssignedToMainTask,
            responsibleId: this.userSession?.userCode,
          };
          if (payload.WOID > 0) {
            fetchData.technicianExecutionWOID = payload.TechnicianExecutionWOID;
            fetchData.WOId = payload.WOID;
          } else {
            fetchData.technicianTaskId = payload.TechnicianTaskId;
          }

          this.technicianService.GetSubTaskWorkOrdersByTaskOrWOID(fetchData).subscribe(
            (subtaskData: any) => {
              this.rowData = subtaskData;
              this.gridApi.setRowData(this.rowData);
              this.gridApi.refreshCells({ force: true });
            },
            error => console.error('Error fetching subtasks:', error)
          );
        },
        error: (err) => {
          console.error('Error updating subtask:', err);
        },
      });
    }
  }

  getLangWords() {
    this.pageId = this.baseApi.getCurrentMenuID();
    const userMenus = this.baseApi.getUserPermissionsSession();
    this.moduleId = userMenus.find(x => x.menuId === this.pageId)?.parentId;

    const key = {
      ModuleId: this.moduleId,
      PageId: this.pageId,
      LanguageId:
        this.translate.currentLang === 'en' ? 1 :
          this.translate.currentLang === 'ar' ? 2 :
            this.translate.currentLang === 'fr' ? 3 :
              this.translate.currentLang === 'sp' ? 4 : 1
    };

    this.translater.getLangWords(key).subscribe((res: any) => {
      const obj: any = {};
      res.tagList.forEach((elem: any) => {
        obj[elem.tagName] = elem.translatedValue;
      });
      this.langObj = obj;
      if (this.gridApi) this.gridApi.refreshHeader();
    });
  }

  localizeHeader(params: any) {
    const field = params.colDef.field;
    return this.langObj?.[field] || field;
  }

  // ✅ Dialog close with completion check
  closeDialog(): void {
    const allCompleted = this.rowData.length > 0
      ? this.rowData.every(row => row.Status === 'Completed')
      : false;

    this.dialogRef.close(allCompleted);
  }
}
